import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const UNIT = { panel: "W", inverter: "kW", battery: "kWh" };
const empty = (type, region = "IN") => ({ type, region, brand: "KyraLink", model: "", rating: "", efficiency: "", price: "", width_mm: "", height_mm: "", notes: "", phase_type: "" });

const Field = ({ label, testid, ...p }) => (<label className="block"><span className="kl-label">{label}</span><input data-testid={testid} className="kl-input-box mt-1" {...p} /></label>);

export default function CatalogForm({ item, region, onOpenChange, onSaved }) {
  const [f, setF] = useState(empty("panel"));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setF({ ...empty(item.type, item.region), ...item, efficiency: item.efficiency ?? "", width_mm: item.width_mm ?? "", height_mm: item.height_mm ?? "", phase_type: item.phase_type ?? "" }); }, [item]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const isPanel = f.type === "panel";
  const isInverter = f.type === "inverter";
  const cur = region?.currency || "";

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { type: f.type, region: f.region, brand: f.brand, model: f.model, rating: parseFloat(f.rating), unit: UNIT[f.type], price: parseFloat(f.price), notes: f.notes || "",
      efficiency: isPanel && f.efficiency !== "" ? parseFloat(f.efficiency) : null, width_mm: isPanel && f.width_mm !== "" ? parseInt(f.width_mm, 10) : null, height_mm: isPanel && f.height_mm !== "" ? parseInt(f.height_mm, 10) : null,
      phase_type: isInverter ? f.phase_type : null };
    try {
      if (item?.id) await api.put(`/catalog/${item.id}`, body); else await api.post("/catalog", body);
      toast.success(item?.id ? "Catalog item updated" : "Added to catalog"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border-kl-black p-0 gap-0" data-testid="catalog-form-dialog">
        <DialogHeader className="bg-kl-black text-white p-6">
          <p className="kl-label-dark">Catalog · {f.type} · <span className="text-kl-green">{region?.name} · {cur}</span></p>
          <DialogTitle className="font-display font-light text-2xl">{item?.id ? "Edit" : "Add"} {f.type}</DialogTitle>
          <DialogDescription className="text-kl-muted">{isPanel ? "Output watts drive the panel count on every client dashboard." : "Rating is used for nearest-fit auto-selection."} Prices in {cur}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <Field label="Brand" testid="catalog-brand-input" required value={f.brand} onChange={set("brand")} />
          <Field label="Model" testid="catalog-model-input" required value={f.model} onChange={set("model")} />
          <Field label={`Rating (${UNIT[f.type]})`} testid="catalog-rating-input" required type="number" step="any" min="0" value={f.rating} onChange={set("rating")} />
          <Field label={`Price (${cur})`} testid="catalog-price-input" required type="number" step="any" min="0" value={f.price} onChange={set("price")} />
          {isPanel && <><Field label="Efficiency (%)" testid="catalog-efficiency-input" type="number" step="0.1" value={f.efficiency} onChange={set("efficiency")} />
            <div className="grid grid-cols-2 gap-2"><Field label="Width mm" testid="catalog-width-input" type="number" value={f.width_mm} onChange={set("width_mm")} /><Field label="Height mm" testid="catalog-height-input" type="number" value={f.height_mm} onChange={set("height_mm")} /></div></>}
          {isInverter && (
            <label className="block">
              <span className="kl-label">Phase compatibility</span>
              <select data-testid="catalog-phase-type-input" required className="kl-input-box mt-1" value={f.phase_type} onChange={set("phase_type")}>
                <option value="" disabled>Select…</option>
                <option value="single">Single phase</option>
                <option value="three">Three phase</option>
                <option value="both">Both</option>
              </select>
            </label>
          )}
          <Field label="Notes" testid="catalog-notes-input" value={f.notes} onChange={set("notes")} />
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="kl-btn-outline" data-testid="catalog-cancel-button">Cancel</button>
            <button disabled={busy} className="kl-btn-primary" data-testid="catalog-submit-button">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
